import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Save, Loader2, Plus, MapPin, Download, Upload } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { assetService } from '../../services/assetService';
import { exportAssetTemplate, parseAssetTemplate } from '../../utils/assetExcel';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import PickerPopover from '../../components/PickerPopover';
import AddAssetModal from './AddAssetModal';

// =============================================================================
// Capital > Asset Register — what the business owns and has to look after.
//
// Lives in Capex (MOD_5) rather than Manufacturing because the register is the
// spine both modules stand on: Capex capitalises against it, Maintenance plans
// against it, and replace-vs-repair is one decision that must never be taken
// against two different lists. Buying the Capital module is what unlocks it.
//
// Not to be confused with Capital > Depreciation Schedule: that is about what
// the budget absorbs, this is about the physical things themselves.
//
// The register is BUDGET data, scoped to the active version: version 2 is
// exactly where you say "we buy a second extruder next year". Asset CLASSES are
// the exception — a taxonomy shared across every version, because maintenance
// job plans are authored against a class and must stay reusable.
//
// THE HIERARCHY IS FIXED AT FOUR LEVELS:
//   site > area > asset > component
// Components are ordinary rows that name a parent machine, shown indented. The
// database caps the depth at one hop and overwrites a component's site and area
// from its parent — so both are read-only on a component row here rather than
// letting someone type a value that will be silently replaced.
//
// Class is the column that matters most downstream: maintenance job plans get
// authored against a CLASS, so one "Forklift 500hr service" covers all forty
// forklifts. The Unclassified chip exists to make that gap visible.
// =============================================================================

const COLUMNS = [
  { id: 'code',   title: 'Asset',             width: 150 },
  { id: 'desc',   title: 'Description',       width: 230 },
  { id: 'class',  title: 'Class',             width: 170 },
  { id: 'site',   title: 'Site',              width: 90 },
  { id: 'area',   title: 'Area',              width: 160 },
  { id: 'cc',     title: 'Cost Centre',       width: 180 },
  { id: 'wc',     title: 'Work Centre',       width: 170 },
  { id: 'crit',   title: 'Crit',              width: 64 },
  { id: 'meter',  title: 'Meter',             width: 90 },
  { id: 'rv',     title: 'Replacement Value', width: 150 },
  { id: 'active', title: 'Active',            width: 64 },
  { id: 'del',    title: 'Delete',            width: 64 },
];
const COL_IDS = COLUMNS.map((c) => c.id);

// Which picker each picker-column draws from, and the field it writes.
const PICKERS = {
  class: 'asset_class_code',
  area: 'area_code',
  cc: 'cost_center_code',
  wc: 'work_center_code',
  crit: 'criticality',
};

const CRITICALITY_OPTIONS = [
  { code: 'A', description: 'Production stops' },
  { code: 'B', description: 'Degraded output' },
  { code: 'C', description: 'Nuisance only' },
];

const T_MUTED = { textDark: '#94a3b8' };
const T_UNSET = { textDark: '#cbd5e1' };
const T_COMPONENT = { textDark: '#64748b', bgCell: 'rgba(100, 116, 139, 0.06)' };
const B_A = { bgBubble: 'rgba(239, 68, 68, 0.16)', textBubble: '#b91c1c' };
const B_B = { bgBubble: 'rgba(245, 158, 11, 0.18)', textBubble: '#b45309' };
const B_C = { bgBubble: 'rgba(100, 116, 139, 0.14)', textBubble: '#64748b' };
const CRIT_THEME = { A: B_A, B: B_B, C: B_C };

const fmtR = (n) => (n === null || n === undefined || n === ''
  ? ''
  : `R ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

const AssetRegister = () => {
  const { selectedSite, selectedVersion } = useAppContext();

  const [assets, setAssets] = useState([]);
  const [areas, setAreas] = useState([]);
  const [classes, setClasses] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [sites, setSites] = useState([]);
  const [dirty, setDirty] = useState({});          // { asset_code: true }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [picker, setPicker] = useState(null);      // { row, colId, x, y, options }
  const [addOpen, setAddOpen] = useState(false);
  const [areaOpen, setAreaOpen] = useState(false);
  const [filterArea, setFilterArea] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);   // { errors[] } | { saved }
  const fileRef = useRef(null);

  const ready = !!selectedVersion;

  const load = useCallback(async () => {
    if (!ready) { setAssets([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await assetService.getAssets();
      if (res?.success) {
        setAssets(res.data.assets || []);
        setAreas(res.data.areas || []);
        setClasses(res.data.classes || []);
        setCostCenters(res.data.costCenters || []);
        setWorkCenters(res.data.workCenters || []);
        setSites(res.data.sites || []);
        setDirty({});
      } else {
        setError(res?.message || 'Failed to load the asset register.');
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load the asset register.');
    } finally {
      setLoading(false);
    }
  }, [ready]);

  // Version and site are both server-side filters, so reload when either moves.
  useEffect(() => { load(); }, [load, selectedSite, selectedVersion]);

  // The grid reads rows through a ref so getCellContent stays referentially
  // stable while editing (same approach as Manufacturing Setup).
  const view = useMemo(() => assets.filter((a) => {
    if (filterArea && a.area_code !== filterArea) return false;
    if (filterClass && a.asset_class_code !== filterClass) return false;
    return true;
  }), [assets, filterArea, filterClass]);

  const viewRef = useRef(view);
  viewRef.current = view;

  const optionsFor = useCallback((colId) => {
    switch (colId) {
      case 'class': return classes.map((c) => ({ code: c.code, description: c.description }));
      case 'area': return areas.map((a) => ({ code: a.code, description: a.description }));
      case 'cc': return costCenters;
      case 'wc': return workCenters;
      case 'crit': return CRITICALITY_OPTIONS;
      default: return [];
    }
  }, [classes, areas, costCenters, workCenters]);

  const labelOf = useCallback((list, code) => {
    if (!code) return '';
    const hit = list.find((o) => o.code === code);
    return hit ? `${hit.code} · ${hit.description}` : code;
  }, []);

  const getCellContent = useCallback(([col, row]) => {
    const r = viewRef.current[row];
    const id = COL_IDS[col];
    if (!r) return gcell.text('', { readonly: true });

    switch (id) {
      case 'code':
        // Components are indented so the parent/child shape reads at a glance.
        return gcell.text(r.is_component ? `  └ ${r.asset_code}` : r.asset_code, {
          readonly: true, theme: r.is_component ? T_COMPONENT : undefined,
        });
      case 'desc':
        return gcell.text(r.description, { theme: r.is_component ? T_COMPONENT : undefined });
      case 'site':
        return gcell.text(r.site_code, { readonly: true, theme: T_MUTED });
      case 'crit': {
        if (!r.criticality) return gcell.text('—', { readonly: true, theme: T_UNSET });
        return gcell.bubble([r.criticality], { theme: CRIT_THEME[r.criticality] });
      }
      case 'meter':
        // Blank on the asset = inherited from its class; shown muted so it is
        // obvious the value is not set here.
        return gcell.text(r.meter_uom || '—', {
          readonly: true, theme: r.meter_uom_own ? undefined : T_UNSET,
        });
      case 'rv':
        return gcell.number(r.replacement_value ?? 0, { display: fmtR(r.replacement_value) });
      case 'active':
        return gcell.boolean(r.is_active);
      case 'del':
        return gcell.text('✕', { readonly: true, theme: T_MUTED });
      case 'area': {
        // The database copies a component's area down from its parent, so
        // editing it here would be a lie.
        if (r.is_component) {
          return gcell.text(r.area_code ? labelOf(optionsFor('area'), r.area_code) : '—', {
            readonly: true, theme: T_COMPONENT,
          });
        }
        const val = r.area_code;
        return gcell.text(val ? labelOf(optionsFor('area'), val) : 'Not set', {
          readonly: true, theme: val ? undefined : T_UNSET,
        });
      }
      default: {
        const field = PICKERS[id];
        if (!field) return gcell.text('', { readonly: true });
        const val = r[field];
        return gcell.text(val ? labelOf(optionsFor(id), val) : 'Not set', {
          readonly: true, theme: val ? undefined : T_UNSET,
        });
      }
    }
  }, [labelOf, optionsFor]);

  const patchAsset = useCallback((assetCode, patch) => {
    setAssets((prev) => prev.map((a) => (a.asset_code === assetCode ? { ...a, ...patch } : a)));
    setDirty((d) => ({ ...d, [assetCode]: true }));
  }, []);

  const onCellEdited = useCallback(([col, row], newValue) => {
    const r = viewRef.current[row];
    const id = COL_IDS[col];
    if (!r) return;

    if (id === 'desc') {
      const text = String(newValue.data ?? '').trim();
      if (!text) return;                       // description is NOT NULL
      patchAsset(r.asset_code, { description: text });
    } else if (id === 'rv') {
      const n = Number(newValue.data);
      patchAsset(r.asset_code, { replacement_value: Number.isNaN(n) ? null : n });
    } else if (id === 'active') {
      patchAsset(r.asset_code, { is_active: !!newValue.data });
    }
  }, [patchAsset]);

  const onCellClicked = useCallback(async ([col, row], event) => {
    const r = viewRef.current[row];
    const id = COL_IDS[col];
    if (!r) return;

    if (id === 'del') {
      const warning = r.component_count > 0
        ? `\n\nIts ${r.component_count} component${r.component_count === 1 ? '' : 's'} will be deleted too.`
        : '';
      const ok = window.confirm(
        `Delete asset ${r.asset_code} · ${r.description}?${warning}\n\nThis cannot be undone.`
      );
      if (!ok) return;
      try {
        const res = await assetService.deleteAsset(r.asset_code);
        if (!res?.success) setError(res?.message || 'Could not delete the asset.');
        else setError('');
        load();
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Could not delete the asset.');
      }
      return;
    }

    if (!PICKERS[id]) return;
    if (id === 'area' && r.is_component) return;   // inherited from the parent

    const b = event?.bounds;
    setPicker({
      row,
      colId: id,
      x: b ? b.x : 240,
      y: b ? b.y + b.height : 200,
      options: optionsFor(id),
    });
  }, [load, optionsFor]);

  const applyPick = (code) => {
    if (!picker) return;
    const r = viewRef.current[picker.row];
    if (r) patchAsset(r.asset_code, { [PICKERS[picker.colId]]: code });
    setPicker(null);
  };

  const handleSave = async () => {
    const changed = assets.filter((a) => dirty[a.asset_code]);
    if (changed.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const res = await assetService.saveAssets({
        assets: changed.map((a) => ({
          asset_code: a.asset_code,
          description: a.description,
          long_description: a.long_description,
          site_code: a.site_code,
          area_code: a.area_code,
          parent_asset_code: a.parent_asset_code,
          asset_class_code: a.asset_class_code,
          cost_center_code: a.cost_center_code,
          work_center_code: a.work_center_code,
          criticality: a.criticality,
          // Send the asset's OWN meter, never the value inherited from its
          // class — otherwise saving would quietly pin the inherited default.
          meter_uom_own: a.meter_uom_own,
          replacement_value: a.replacement_value,
          commissioned_date: a.commissioned_date,
          manufacturer: a.manufacturer,
          model: a.model,
          serial_number: a.serial_number,
          is_active: a.is_active,
          notes: a.notes,
        })),
      });
      if (res?.success) { setDirty({}); load(); }
      else setError(res?.message || 'Save failed.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Excel round trip ----------------------------------------------------
  // Exports what is currently LOADED (so the site context carries through), not
  // the filtered view — a filtered export re-imported would look like a delete.
  const handleExport = async () => {
    setError('');
    try {
      await exportAssetTemplate({
        assets, sites, areas, classes, costCenters, workCenters,
        versionLabel: selectedVersion,
      });
    } catch (e) {
      setError(e.message || 'Could not build the spreadsheet.');
    }
  };

  const handleImportFile = async (file) => {
    if (!file) return;
    setImporting(true);
    setError('');
    setImportReport(null);
    try {
      const { rows, errors } = await parseAssetTemplate(file, {
        sites, areas, classes, costCenters, workCenters,
      });
      if (errors.length) { setImportReport({ errors }); return; }
      if (rows.length === 0) { setImportReport({ errors: ['The file had no asset rows.'] }); return; }

      const res = await assetService.saveAssets({ assets: rows });
      if (res?.success) {
        setImportReport({ saved: res.savedCount ?? rows.length });
        load();
      } else {
        setImportReport({ errors: [res?.message || 'The import was rejected.'] });
      }
    } catch (e) {
      setImportReport({ errors: [e.response?.data?.message || e.message || 'Could not read the file.'] });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';   // let the same file be re-picked
    }
  };

  // ---- Summary -------------------------------------------------------------
  const stats = useMemo(() => {
    const machines = assets.filter((a) => !a.is_component);
    return {
      machines: machines.length,
      components: assets.length - machines.length,
      critA: assets.filter((a) => a.criticality === 'A').length,
      unclassified: assets.filter((a) => !a.asset_class_code).length,
      rav: assets.reduce((s, a) => s + (Number(a.replacement_value) || 0), 0),
    };
  }, [assets]);

  const hasChanges = Object.keys(dirty).length > 0;
  const siteAreas = useMemo(
    () => areas.filter((a) => !selectedSite || a.site_code === selectedSite),
    [areas, selectedSite]
  );

  const selectClass = 'text-[11px] rounded-md border border-slate-300 dark:border-slate-600 '
    + 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-2 py-1 outline-none';

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <h1
            className="m-0 mr-1 text-lg font-bold leading-7 tracking-tight text-slate-900 dark:text-slate-100"
            title="Everything this site maintains — the register maintenance planning and Capex both hang off."
          >
            Asset Register
          </h1>
          {stats.unclassified > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              {stats.unclassified} unclassified
            </span>
          )}
          {error && <span className="text-[11px] text-red-600 dark:text-red-400">{error}</span>}

          <div className="ml-auto flex items-center gap-2">
            <select className={selectClass} value={filterArea} onChange={(e) => setFilterArea(e.target.value)}>
              <option value="">All areas</option>
              {siteAreas.map((a) => (
                <option key={`${a.site_code}-${a.code}`} value={a.code}>{a.code} · {a.description}</option>
              ))}
            </select>
            <select className={selectClass} value={filterClass} onChange={(e) => setFilterClass(e.target.value)}>
              <option value="">All classes</option>
              {classes.map((c) => <option key={c.code} value={c.code}>{c.description}</option>)}
            </select>
            <button
              type="button"
              onClick={handleExport}
              disabled={!ready || assets.length === 0}
              title="Download the register as an Excel workbook with dropdowns"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <Download className="w-3 h-3" /> Export
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={!ready || importing}
              title="Import assets from a filled-in template"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {importing ? 'Importing…' : 'Import'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
            <button
              type="button"
              onClick={() => setAreaOpen(true)}
              disabled={!selectedSite}
              title={selectedSite ? 'Add an area to this site' : 'Select a site first'}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <MapPin className="w-3 h-3" /> Add area
            </button>
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              disabled={!ready}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              <Plus className="w-3 h-3" /> Add asset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md text-white shadow-sm transition-colors ${
                hasChanges && !saving ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400">
          <span><strong className="text-slate-700 dark:text-slate-200">{stats.machines}</strong> assets</span>
          <span><strong className="text-slate-700 dark:text-slate-200">{stats.components}</strong> components</span>
          <span><strong className="text-slate-700 dark:text-slate-200">{stats.critA}</strong> critical (A)</span>
          <span>Replacement value <strong className="text-slate-700 dark:text-slate-200 font-mono">{fmtR(stats.rav) || 'R 0'}</strong></span>
          {!selectedSite && <span className="text-slate-400">Showing every site — select one in the top bar to narrow.</span>}
        </div>

        {/* Import outcome. Errors are listed per row so a bad spreadsheet can be
            fixed in place rather than by trial and error. */}
        {importReport && (
          <div className={`mt-2 rounded-lg border px-3 py-2 text-[11px] ${
            importReport.errors
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {importReport.errors ? (
                  <>
                    <strong>Import rejected — nothing was saved.</strong>
                    <ul className="m-0 mt-1 pl-4 list-disc max-h-32 overflow-y-auto">
                      {importReport.errors.slice(0, 25).map((msg, i) => <li key={i}>{msg}</li>)}
                    </ul>
                    {importReport.errors.length > 25 && (
                      <p className="m-0 mt-1 opacity-70">…and {importReport.errors.length - 25} more.</p>
                    )}
                  </>
                ) : (
                  <strong>Imported {importReport.saved} asset{importReport.saved === 1 ? '' : 's'}.</strong>
                )}
              </div>
              <button
                type="button"
                onClick={() => setImportReport(null)}
                className="shrink-0 font-semibold opacity-60 hover:opacity-100"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-4">
        <div className="h-full max-h-full flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
          {!ready ? (
            <div className="p-10 text-center text-sm text-slate-400">
              Select a budget version in the top bar to begin.<br />
              <span className="text-xs">The register is per version — that is how a version can plan for assets you have not bought yet.</span>
            </div>
          ) : loading ? (
            <div className="p-10 text-center text-sm text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : view.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">
              {assets.length === 0 ? (
                <>
                  No assets registered yet.<br />
                  <span className="text-xs">
                    Start with the machines and services this site maintains — maintenance job plans hang off these.
                  </span><br />
                  <button
                    type="button"
                    onClick={() => setAddOpen(true)}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add the first asset
                  </button>
                </>
              ) : (
                <>Nothing matches those filters.</>
              )}
            </div>
          ) : (
            <MantleDataGrid
              gridId="asset-register"
              columns={COLUMNS}
              rowCount={view.length}
              getCellContent={getCellContent}
              onCellEdited={onCellEdited}
              onCellClicked={onCellClicked}
              freezeColumns={1}
              hint="Click Class, Area, Cost Centre, Work Centre or Crit to pick a value; type directly into Description and Replacement Value. Components are indented and inherit their parent's site and area. ✕ deletes the asset and its components."
            />
          )}
        </div>
      </div>

      {addOpen && (
        <AddAssetModal
          siteCode={selectedSite}
          existingCodes={assets.map((a) => a.asset_code)}
          assets={assets}
          areas={areas}
          classes={classes}
          onAdded={() => { setAddOpen(false); load(); }}
          onClose={() => setAddOpen(false)}
        />
      )}

      {areaOpen && (
        <AddAreaModal
          siteCode={selectedSite}
          existing={siteAreas}
          onAdded={() => { setAreaOpen(false); load(); }}
          onClose={() => setAreaOpen(false)}
        />
      )}

      {picker && (
        <PickerPopover
          anchor={picker}
          options={picker.options}
          onPick={applyPick}
          onClear={() => applyPick(null)}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

// Areas are level 2 of the hierarchy — a line, a wing, a yard. Kept as real
// records rather than free text so a typo cannot fragment the roll-ups.
const AddAreaModal = ({ siteCode, existing = [], onAdded, onClose }) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const trimmed = code.trim().toUpperCase();
  const duplicate = existing.some((a) => a.code.toUpperCase() === trimmed);
  const canAdd = trimmed && description.trim() && !duplicate && !busy;

  const submit = async () => {
    if (!canAdd) return;
    setBusy(true);
    setError('');
    try {
      const res = await assetService.saveAreas({
        areas: [{ site_code: siteCode, code: trimmed, description: description.trim() }],
      });
      if (res?.success) onAdded();
      else setError(res?.message || 'Could not add the area.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not add the area.');
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 '
    + 'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-sm rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95">
          <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add area</h3>
          <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            A named part of {siteCode || 'this site'} — a production line, a wing, a yard.
          </p>
        </div>
        <div className="p-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Code
            <input
              type="text" className={`${inputClass} font-mono`} placeholder="e.g. LINE2"
              value={code} disabled={busy} autoFocus
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Description
            <input
              type="text" className={inputClass} placeholder="e.g. Bottling Line 2"
              value={description} disabled={busy}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>
          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">
            {duplicate ? 'That area code already exists at this site.' : error}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {busy ? 'Adding…' : 'Add area'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetRegister;
